import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

import { paymentFeedApi, paymentFeedQueryKeys } from "../../shared/api/platformBilling";
import {
  eInvoiceApi,
  eInvoiceQueryKeys,
  saasInvoiceApi,
  saasInvoiceQueryKeys,
  type InvoiceQueueRow,
  type IssueOutcome,
} from "../../shared/api/platformBilling";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const VND = new Intl.NumberFormat("vi-VN");
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("vi-VN") : "—");

/**
 * Hàng đợi hoá đơn GTGT Addy phải xuất cho phòng khám (#18990).
 *
 * <p>Khai tài khoản hoá đơn điện tử rồi thì Addy tự phát hành (#18990) — nút ghi số bằng tay VẪN
 * ở lại, vì một hoá đơn nhà cung cấp từ chối mãi vẫn phải có đường cho con người xử lý.
 *
 * <p>Ba trạng thái được tách bạch vì chúng cần ba hành động khác nhau: <b>Chờ xuất</b> là việc của
 * hệ thống hoặc kế toán; <b>Đã gửi, chờ số</b> không cần ai làm gì ngoài chờ — và tuyệt đối không
 * được gửi lại; <b>Thiếu mã số thuế</b> là việc phải đi ĐÒI dữ liệu từ khách trước đã. Gộp chung
 * thì hàng đợi nói dối về việc còn phải làm gì.
 */
export function PlatformInvoiceQueuePage() {
  const q = useQuery({
    queryKey: saasInvoiceQueryKeys.queue(),
    queryFn: () => saasInvoiceApi.queue(),
  });
  const setting = useQuery({
    queryKey: paymentFeedQueryKeys.settings(),
    queryFn: () => paymentFeedApi.settings(),
  });
  const issuing = setting.data?.vatInvoiceEnabled ?? false;
  const config = useQuery({
    queryKey: eInvoiceQueryKeys.config(),
    queryFn: () => eInvoiceApi.config(),
  });
  const provider = (config.data ?? []).find((c) => c.status === "ACTIVE") ?? null;

  const qc = useQueryClient();
  const [outcomes, setOutcomes] = useState<IssueOutcome[] | null>(null);
  const issueAll = useMutation({
    mutationFn: () => eInvoiceApi.issuePending(),
    onSuccess: (res) => {
      setOutcomes(res);
      void qc.invalidateQueries({ queryKey: saasInvoiceQueryKeys.queue() });
    },
  });

  const rows = q.data ?? [];
  const pending = rows.filter((r) => r.status === "PENDING");
  const submitted = rows.filter((r) => r.status === "SUBMITTED");
  const blocked = rows.filter((r) => r.status === "BLOCKED");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-foreground">Hoá đơn GTGT</h1>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          Khoản phòng khám đã trả cho Addy nhưng hoá đơn chưa xong.{" "}
          {provider
            ? `Addy tự phát hành qua ${provider.provider}; ghi số bằng tay vẫn dùng được khi nhà cung cấp từ chối.`
            : "Chưa khai tài khoản hoá đơn điện tử — hôm nay xuất trên cổng nhà cung cấp rồi ghi số về đây."}
        </p>
      </div>

      {provider && issuing && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 p-3 text-sm">
          <span>
            Đến lượt định kỳ 15 phút một lần. Bấm để chạy ngay, không phải chờ.
          </span>
          <Button
            size="sm"
            className="h-8"
            disabled={issueAll.isPending || rows.length === 0}
            onClick={() => issueAll.mutate()}
          >
            {issueAll.isPending ? "Đang phát hành…" : "Phát hành tất cả"}
          </Button>
          {issueAll.isError && (
            <span className="text-xs text-destructive">{apiError(issueAll.error).detail}</span>
          )}
        </div>
      )}
      {outcomes && outcomes.length > 0 && (
        <div className="space-y-1 rounded-md border p-3 text-xs">
          {/* Từng dòng, không phải một con số: một lần chạy điển hình có dòng phát hành được,
              dòng bị chặn vì thiếu MST, và dòng nhà cung cấp từ chối. */}
          {outcomes.map((o) => (
            <div key={o.invoiceId} className="flex gap-2">
              <span className="w-24 shrink-0 font-medium">{o.status}</span>
              <span className="text-muted-foreground">{o.detail}</span>
            </div>
          ))}
        </div>
      )}

      {!issuing && (
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <b>Chưa bật xuất hoá đơn GTGT.</b> Danh sách dưới đây vẫn đầy đủ — mọi kỳ đã thu tiền đều
          được ghi nhận, không kỳ nào mất dấu. Chỉ là chưa có nút ghi số hoá đơn. Bật ở{" "}
          <b>Thanh toán → Hoá đơn GTGT</b>.
        </div>
      )}
      {q.isError && <p className="text-sm text-destructive">{apiError(q.error).detail}</p>}
      {q.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}

      {!q.isLoading && rows.length === 0 && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-600/30 bg-emerald-600/5 p-3 text-sm">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
          <span>
            <b>Không còn kỳ nào chờ hoá đơn.</b> Mọi kỳ đã thu tiền đều đã xuất hoặc đã đánh dấu
            khách không lấy hoá đơn.
          </span>
        </div>
      )}

      {blocked.length > 0 && (
        <Section
          issuing={issuing}
          tone="blocked"
          title={`${blocked.length} kỳ THIẾU MÃ SỐ THUẾ — phải đòi dữ liệu khách trước`}
          rows={blocked}
        />
      )}
      {pending.length > 0 && (
        <Section
          issuing={issuing}
          tone="pending"
          title={`${pending.length} khoản chờ xuất`}
          rows={pending}
        />
      )}
      {submitted.length > 0 && (
        <Section
          issuing={issuing}
          tone="submitted"
          title={`${submitted.length} khoản ĐÃ GỬI nhà cung cấp, đang chờ số hoá đơn`}
          rows={submitted}
        />
      )}
    </div>
  );
}

function Section({
  tone,
  title,
  rows,
  issuing,
}: {
  tone: "pending" | "submitted" | "blocked";
  title: string;
  rows: InvoiceQueueRow[];
  issuing: boolean;
}) {
  return (
    <div className="space-y-2">
      <h2
        className={
          tone === "blocked"
            ? "flex items-center gap-1.5 text-sm font-semibold text-amber-700"
            : "text-sm font-semibold text-foreground"
        }
      >
        {tone === "blocked" && <AlertTriangle className="h-4 w-4" />}
        {title}
      </h2>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[60rem] text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Phòng khám</th>
              <th className="px-3 py-2 text-left font-medium">Mã số thuế</th>
              <th className="px-3 py-2 text-right font-medium">Số tiền</th>
              <th className="px-3 py-2 text-left font-medium">Kỳ</th>
              <th className="px-3 py-2 text-left font-medium">
                {tone === "submitted" ? "Mã tra cứu" : "Ghi số hoá đơn"}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <InvoiceRow key={r.id} row={r} issuing={issuing} tone={tone} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvoiceRow({
  row,
  issuing,
  tone,
}: {
  row: InvoiceQueueRow;
  issuing: boolean;
  tone: "pending" | "submitted" | "blocked";
}) {
  const qc = useQueryClient();
  const [invoiceNo, setInvoiceNo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const issueNow = useMutation({
    mutationFn: () => eInvoiceApi.issueOne(row.id),
    onSuccess: (res) => {
      setError(null);
      setOutcome(res.detail);
      void qc.invalidateQueries({ queryKey: saasInvoiceQueryKeys.queue() });
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const record = useMutation({
    mutationFn: (status: "ISSUED" | "NOT_REQUIRED") =>
      saasInvoiceApi.record(row.id, {
        status,
        invoiceNo: status === "ISSUED" ? invoiceNo.trim() : null,
        note: status === "NOT_REQUIRED" ? "Khách không lấy hoá đơn" : null,
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: saasInvoiceQueryKeys.queue() });
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const refresh = useMutation({
    mutationFn: () => saasInvoiceApi.refreshBuyer(row.id),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: saasInvoiceQueryKeys.queue() });
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const canIssue = invoiceNo.trim() !== "";

  return (
    <tr className="border-t align-top">
      <td className="px-3 py-2">
        <div className="font-medium text-foreground">{row.buyerName ?? "—"}</div>
        <div className="text-xs text-muted-foreground">{row.tenantCode ?? "—"}</div>
        {row.buyerAddress && (
          <div className="mt-0.5 max-w-xs text-xs text-muted-foreground">{row.buyerAddress}</div>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {row.buyerTaxCode ?? (
          <span className="whitespace-nowrap text-amber-700">chưa có</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{VND.format(row.amountVnd)}đ</td>
      <td className="px-3 py-2 whitespace-nowrap text-xs">
        {day(row.periodStarts)} – {day(row.periodEnds)}
      </td>
      <td className="px-3 py-2">
        {tone === "submitted" && (
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs">{row.lookupCode ?? "—"}</span>
            {/* Chỉ TRA SỐ. Gửi lại một hoá đơn đã gửi là xuất trùng cho cùng một khoản thu. */}
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={issueNow.isPending}
              onClick={() => issueNow.mutate()}
            >
              {issueNow.isPending ? "Đang tra…" : "Tra số ngay"}
            </Button>
          </div>
        )}
        {tone === "pending" && (
          <div className="mb-1">
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={issueNow.isPending}
              onClick={() => issueNow.mutate()}
            >
              {issueNow.isPending ? "Đang phát hành…" : "Phát hành ngay"}
            </Button>
          </div>
        )}
        {outcome && <p className="mb-1 text-xs text-muted-foreground">{outcome}</p>}
        {!issuing ? (
          <span className="text-xs text-muted-foreground">Chưa bật xuất hoá đơn</span>
        ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="h-8 w-36"
            placeholder="Số hoá đơn"
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
            disabled={row.status === "BLOCKED"}
          />
          <Button
            size="sm"
            className="h-8"
            disabled={!canIssue || record.isPending || row.status === "BLOCKED"}
            onClick={() => record.mutate("ISSUED")}
          >
            Đã xuất
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={record.isPending}
            onClick={() => record.mutate("NOT_REQUIRED")}
          >
            Không lấy HĐ
          </Button>
        </div>
        )}
        {row.status === "BLOCKED" && (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="text-xs text-amber-700">
              Cần mã số thuế của phòng khám trước khi xuất được.
            </p>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              disabled={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              {refresh.isPending ? "Đang đọc…" : "Khách đã bổ sung — đọc lại"}
            </Button>
          </div>
        )}
        {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
      </td>
    </tr>
  );
}
